import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { ApiResponse } from "@/services/api/types";
import { CoreService } from "@/services/core/CoreService";
import { isCraveUser } from "@/utils/permissionManager";

export interface Store {
  id: number;
  name: string;
  customerId: number;
  customerName: string;
  customerInitial?: string | null;
  live: boolean;
  active: boolean;
  address?: string;
  autoCleanEnabled: boolean;
  city: string;
  country?: string;
  currencySymbol?: string;
  currencyCode?: string;
  locale?: string;
  metadataMap?: Record<string, string>;
  externalId: string;
  geoPoint: { lat: number; lon: number };
  global: boolean;
  group: string;
  inProgressUploadCount: number;
  openDate: string;
  shouldRestartShopper: boolean;
  state: string;
  street: string;
  successfulUploadCount: number;
  timezone: string;
  uploadDelta: number;
  zip: string;
  phoneNumber?: string;
  regionId?: number;
}

export type CreateStoreRequest = Omit<Store, "id" | "geoPoint"> & {
  geoPoint: { lat: number; lon: number };
};

export interface Region {
  id: number;
  name: string;
  storeId?: number;
}

export interface Customer {
  id: number;
  name: string;
  initial?: string | null;
  live: boolean;
}

export interface ChangingRoom {
  id?: number;
  name: string;
  type:
    | "FITTING_ROOM"
    | "IN_AISLE"
    | "MOBILE_PICKUP"
    | "RUNNER_PICKUP"
    | "RFID_CENTRAL"
    | "CLICK_AND_COLLECT"
    | "FITTING_ROOM_ANTENNA_ONLY";
  storeId?: number;
  groupId?: number;
  assignedAt?: number;
  requests?: any[];
}

export interface ChangingRoomGroup {
  id?: number;
  name: string;
  storeId: number;
  changingRoomGroup: { id?: number; name: string; storeId: number };
  changingRooms: ChangingRoom[];
}

export interface StoreResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const buildDisableStoreEndpoint = (storeId: number): string =>
  `/store/${storeId}/active/false`;

export interface ChangingRoomGroupUpdateData {
  name: string;
  id?: number;
  rooms: Omit<ChangingRoom, "storeId" | "groupId" | "assignedAt" | "requests">[];
}

export class StoreService extends BaseApiService {
  private static instance: StoreService;
  protected readonly coreService: CoreService;
  private readonly authorization: string;
  private storesRequest: Promise<ApiResponse<Store[]>> | null = null;
  private customerInitialsRequest: Promise<Map<number, string>> | null = null;

  private constructor() {
    super();
    this.coreService = CoreService.getInstance();
    const user = this.coreService.getStoredUser();
    this.authorization = user?.token || "";
  }

  public static getInstance(): StoreService {
    if (!StoreService.instance) {
      StoreService.instance = new StoreService();
    }
    return StoreService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  private async getCustomerInitials(): Promise<Map<number, string>> {
    if (!this.customerInitialsRequest) {
      const user = this.coreService.getStoredUser();
      const customersRequest =
        !isCraveUser(user?.role || "") && user?.customerId
          ? this.getCustomerById(user.customerId).then((customer) => [customer])
          : this.getCustomers().then((response) => response.data);
      const request = customersRequest
        .then(
          (customers) =>
            new Map(
              customers
                .filter((customer): customer is Customer & { initial: string } =>
                  Boolean(customer.initial?.trim()),
                )
                .map((customer) => [customer.id, customer.initial.trim()]),
            ),
        )
        .catch(() => new Map<number, string>());
      this.customerInitialsRequest = request;
      void request.then(() => {
        if (this.customerInitialsRequest === request) this.customerInitialsRequest = null;
      });
    }

    return this.customerInitialsRequest;
  }

  private async attachCustomerInitials(stores: Store[]): Promise<Store[]> {
    const customerInitials = await this.getCustomerInitials();

    return stores.map((store) => ({
      ...store,
      customerInitial: store.customerInitial ?? customerInitials.get(store.customerId),
    }));
  }

  public async getStores(): Promise<ApiResponse<Store[]>> {
    if (!this.storesRequest) {
      const request = this.get<{ data: Store[] }>("/store", this.getCoreBaseUrl())
        .then(async (response) => ({
          data: await this.attachCustomerInitials(response.data),
          metadata: { code: 200, message: "Success" },
        }))
        .catch((error) => {
          this.storesRequest = null;
          throw error;
        });
      this.storesRequest = request;
      void request.then(
        () => {
          if (this.storesRequest === request) this.storesRequest = null;
        },
        () => undefined,
      );
    }

    return this.storesRequest;
  }

  public async getCustomerById(customerId: number): Promise<Customer> {
    try {
      const response = await this.get<ApiResponse<Customer>>(
        `/customer/${customerId}`,
        this.getCoreBaseUrl(),
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCustomersByStores(stores: Store[]): Promise<Customer[]> {
    const uniqueCustomerIds = [...new Set(stores.map((store) => store.customerId))];
    const customers: Customer[] = [];
    for (const customerId of uniqueCustomerIds) {
      const customer = await this.getCustomerById(customerId);
      customers.push(customer);
    }
    return customers;
  }

  public async getCustomers(): Promise<ApiResponse<Customer[]>> {
    const user = this.coreService.getStoredUser();

    // Non-Crave user flow - use customer ID and name from Auth directly
    if (!isCraveUser(user?.role || "") && user?.customerId) {
      return {
        data: [{ id: user.customerId, name: user.customerName || "", live: true }],
        metadata: { code: 200, message: "Success" },
      };
    }

    // Crave user flow - make a direct API call
    try {
      return await this.get<ApiResponse<Customer[]>>(`/customer`, this.getCoreBaseUrl());
    } catch (error) {
      throw error;
    }
  }

  public async getStoresByCustomer(customerId: number): Promise<ApiResponse<Store[]>> {
    try {
      const response = await this.get<{ data: Store[] }>(
        `/store/customer/${customerId}`,
        this.getCoreBaseUrl(),
      );
      return {
        data: await this.attachCustomerInitials(response.data),
        metadata: { code: 200, message: "Success" },
      };
    } catch (error) {
      throw error;
    }
  }

  public async getStoreById(storeId: number): Promise<ApiResponse<Store>> {
    try {
      const response = await this.get<{ data: Store }>(`/store/${storeId}`, this.getCoreBaseUrl());
      const store = (await this.attachCustomerInitials([response.data]))[0] ?? response.data;

      return {
        data: store,
        metadata: { code: 200, message: "Success" },
      };
    } catch (error) {
      throw error;
    }
  }

  public async getChangingRoomsByStore(
    storeId: number,
  ): Promise<StoreResponse<ChangingRoomGroup[]>> {
    try {
      const response = await this.get<{ data: ChangingRoomGroup[] }>(
        `/changingRoomGroup/store/${storeId}`,
        this.getCoreBaseUrl(),
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch changing rooms",
      };
    }
  }

  public async getRegionsByCustomerId(customerId: number): Promise<StoreResponse<Region[]>> {
    try {
      const response = await this.get<{ data: Region[] }>(
        `/region/customer/${customerId}`,
        this.getCoreBaseUrl(),
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch regions",
      };
    }
  }

  public async editStore(
    store: Omit<Partial<Store>, "locale"> & { id: number; locale?: string | null },
  ): Promise<StoreResponse<Store>> {
    try {
      const { id, ...storeUpdates } = store;
      const response = await this.put<{ data: Store }>(
        `/v2/store`,
        {
          storeId: id,
          ...storeUpdates,
        },
        this.getCoreBaseUrl(),
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update store",
      };
    }
  }

  public async disableStoreById(storeId: number): Promise<StoreResponse<void>> {
    try {
      await this.fetchWithAuth(
        buildDisableStoreEndpoint(storeId),
        { method: "PATCH" },
        this.getCoreBaseUrl(),
      );
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to disable store",
      };
    }
  }

  public async addStore(storeData: CreateStoreRequest): Promise<StoreResponse<Store>> {
    try {
      const response = await this.post<{ data: Store }>(`/store`, storeData, this.getCoreBaseUrl());

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create store",
      };
    }
  }

  public async updateChangingRoomGroups(
    storeId: number,
    groups: ChangingRoomGroupUpdateData[],
  ): Promise<StoreResponse<ChangingRoomGroup[]>> {
    try {
      const response = await this.put<{ data: ChangingRoomGroup[] }>(
        `/changingRoomGroup/store`,
        { storeId, groups },
        this.getCoreBaseUrl(),
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update changing rooms",
      };
    }
  }

  public async uploadImage(formData: FormData): Promise<StoreResponse<string>> {
    try {
      const authHeader = this.coreService.getAuthorizationHeader();

      if (!authHeader) {
        return {
          success: false,
          error: "You must be logged in to access this resource",
        };
      }

      const token = authHeader.startsWith("Bearer ") ? authHeader : `Bearer ${authHeader}`;
      const config = getApiConfig();
      const response = await fetch(`${config.core}/photo/upload`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: token,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API request failed: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        return {
          success: false,
          error: errorMessage,
        };
      }

      const responseData = await response.json();
      const uploadedUrl = responseData?.data;
      if (typeof uploadedUrl !== "string") {
        return {
          success: false,
          error: "Failed to upload image",
        };
      }

      return {
        success: true,
        data: uploadedUrl,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to upload image",
      };
    }
  }
}
